import { useAuthStore } from '@/domains/auth';
import { AppError } from '@/lib/api/types/errors';
import { useToast } from '@/shared/hooks/useToast';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { CheckCircle2, Circle, Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Self-service change-password dialog for the logged-in user's own profile
 * (WRK-5238). On success the backend has already revoked every session and
 * cleared the auth cookies, so we clear local auth state and send the user to
 * the login page to re-authenticate with their new credentials.
 */
export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const changePassword = useAuthStore((state) => state.changePassword);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validity = useMemo(
    () => ({
      hasLength: newPassword.length >= 12,
      hasUpper: /[A-Z]/.test(newPassword),
      hasLower: /[a-z]/.test(newPassword),
      hasNumber: /\d/.test(newPassword),
      hasSpecial: /[!@#$%^&*_+\-=|<>/?~]/.test(newPassword),
      passwordMatch: confirmPassword.length > 0 ? newPassword === confirmPassword : true,
    }),
    [newPassword, confirmPassword],
  );

  const allValid =
    currentPassword.length > 0 &&
    validity.hasLength &&
    validity.hasUpper &&
    validity.hasLower &&
    validity.hasNumber &&
    validity.hasSpecial &&
    validity.passwordMatch &&
    confirmPassword.length > 0;

  const requirements = [
    { key: 'hasLength', label: '12 or more characters', met: validity.hasLength },
    { key: 'hasUpper', label: 'At least 1 uppercase letter', met: validity.hasUpper },
    { key: 'hasLower', label: 'At least 1 lowercase letter', met: validity.hasLower },
    { key: 'hasNumber', label: 'At least 1 numeric character', met: validity.hasNumber },
    { key: 'hasSpecial', label: 'At least 1 special character', met: validity.hasSpecial },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await changePassword({
        previousPassword: currentPassword,
        proposedPassword: newPassword,
      });
      // Session is dead server-side — leave the toast up and redirect to login.
      toast({
        title: 'Password Changed',
        description: 'Your password has been changed. Please log in with your new password.',
        className:
          'bg-green-50 border-green-200 text-green-900 dark:bg-green-900 dark:text-green-100',
      });
      onOpenChange(false);
      navigate('/login', { replace: true });
    } catch (err) {
      // The backend returns 403 (NotAuthorizedException) when the current
      // password is wrong — surface that specifically rather than a generic error.
      const isWrongCurrent = err instanceof AppError && err.statusCode === 403;
      toast({
        title: 'Failed to Change Password',
        description: isWrongCurrent
          ? 'Your current password is incorrect. Please try again.'
          : 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  // Reset transient field state whenever the dialog is closed.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      setIsSubmitting(false);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Changing your password will sign you out of all sessions. You'll need to log in again
            with your new password.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword" className="text-sm font-medium">
              Current Password
            </Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                disabled={isSubmitting}
                className="pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password requirements:
            </p>
            {requirements.map((req) => (
              <div key={req.key} className="flex items-center gap-2 text-sm">
                {req.met ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-400" />
                )}
                <span
                  className={
                    req.met
                      ? 'text-green-700 dark:text-green-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }
                >
                  {req.label}
                </span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-sm font-medium">
              New Password
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={isSubmitting}
                className="pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium">
              Confirm New Password
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isSubmitting}
                className={`pr-10 ${!validity.passwordMatch ? 'border-red-500' : ''}`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {!validity.passwordMatch && (
              <p className="text-xs text-red-500">Passwords do not match</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !allValid}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing…
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
